// @flow

import { css } from "styled-components";
import { formatPrice } from "@nested/utils";
import { Button, ButtonLink } from "@nested/component-library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft } from "@fortawesome/free-solid-svg-icons";
import { type Results as ResultsType } from "./calculator";
import { sectionHeading, smallHeading } from "../Typography/Headings";
import { smallParagraph } from "../Typography/Paragraphs";

type Props = {
  results?: ResultsType,
  backToForm: () => void,
  buttonText: string,
};

const sectionHeadingStyle = css`
  ${sectionHeading}
  margin-bottom: 0px;
`;

const savingsStyle = css`
  ${smallHeading}
  font-size: 30px;
  color: ${({ theme }) => theme.palette.green500};
  margin-top: 10px;
`;

const hrStyle = css`
  margin: 0px 0px 20px;
  border: none;
  height: 1px;
  background-color: ${({ theme }) => theme.palette.hague20};
`;

const smallParagraphStyle = css`
  ${smallParagraph};
  margin-top: 0px;
`;

const backButtonStyle = css`
  display: block;
  text-decoration: underline;
  margin-top: 10px;
  padding-left: 0px;
  margin-bottom: 10px;
`;

const ctaStyle = css`
  display: block;
  text-align: center;
`;

export const Results = ({ results, backToForm, buttonText }: Props) => {
  if (!results) {
    backToForm();
    return null;
  }
  const { previousSdlt, sdltWithHoliday, saving } = results;
  return (
    <div>
      <Button
        css={backButtonStyle}
        type="link"
        htmlType="button"
        onClick={backToForm}
      >
        <FontAwesomeIcon css="padding-right: 5px;" icon={faCaretLeft} />
        Back to the calculator
      </Button>
      <div css={sectionHeadingStyle}>New stamp duty</div>
      <p css={smallParagraphStyle}>
        You'll pay this if you complete your purchase after the 23rd of
        September 2022.
      </p>
      <p css={smallHeading} data-test="tax-with-holiday">
        {formatPrice(sdltWithHoliday)}
      </p>

      <hr css={hrStyle} />
      <div css={sectionHeadingStyle}>Previous Stamp Duty</div>
      <p css={smallParagraphStyle}>
        Previously, you would have had to pay this much stamp duty.
      </p>
      <p css={smallHeading} data-test="tax-without-holiday">
        {formatPrice(previousSdlt)}
      </p>

      <hr css={hrStyle} />

      <div css={sectionHeadingStyle}>Total savings</div>
      <p css={savingsStyle} data-test="tax-saving">
        {formatPrice(saving)}
      </p>
      <p css={smallParagraphStyle}>
        <em>
          Special rates apply for those buying non-residential property. See{" "}
          <a
            href="https://www.gov.uk/stamp-duty-land-tax/residential-property-rates"
            target="_blank"
          >
            gov.uk
          </a>{" "}
          for more details.
        </em>
      </p>

      <ButtonLink size="large" css={ctaStyle} type="primary" to="/get-started">
        {buttonText}
      </ButtonLink>
    </div>
  );
};
